import React from 'react';
import Icon from 'components/AppIcon';

const PracticeAnalytics = ({ selectedPeriod }) => {
  const analyticsData = [
    {
      id: 1,
      title: "Client Retention Rate",
      value: "92.8%",
      change: "+3.2% from last period",
      changeType: "positive",
      icon: "UserCheck",
      description: "Clients retained over 12 months"
    },
    {
      id: 2,
      title: "Average Case Value",
      value: "KES 450,000",
      change: "+18% increase",
      changeType: "positive",
      icon: "TrendingUp",
      description: "Mean value across all case types"
    },
    {
      id: 3,
      title: "Resource Utilization",
      value: "84.6%",
      change: "Optimal range",
      changeType: "neutral",
      icon: "Activity",
      description: "Associates and support staff efficiency"
    },
    {
      id: 4,
      title: "Case Resolution Time",
      value: "45 days",
      change: "-8 days improvement",
      changeType: "positive",
      icon: "Clock",
      description: "Average time to case closure"
    }
  ];

  const matterProfitability = [
    { type: "Corporate Mergers", profit: "High", volume: 12, avgValue: 850000, trend: "up" },
    { type: "Real Estate", profit: "High", volume: 23, avgValue: 320000, trend: "up" },
    { type: "Litigation", profit: "Medium", volume: 45, avgValue: 180000, trend: "stable" },
    { type: "Family Law", profit: "Medium", volume: 67, avgValue: 95000, trend: "up" },
    { type: "Criminal Defense", profit: "Low", volume: 34, avgValue: 65000, trend: "down" },
    { type: "Employment", profit: "Medium", volume: 28, avgValue: 125000, trend: "up" }
  ];

  const resourceMetrics = [
    { resource: "Senior Associates", count: 8, utilization: 91, billableRate: 15000 },
    { resource: "Junior Associates", count: 12, utilization: 87, billableRate: 8500 },
    { resource: "Paralegals", count: 6, utilization: 78, billableRate: 4200 },
    { resource: "Support Staff", count: 15, utilization: 85, billableRate: 2800 }
  ];

  const getProfitColor = (profit) => {
    switch (profit) {
      case 'High': return 'text-success';
      case 'Medium': return 'text-warning-600';
      case 'Low': return 'text-error';
      default: return 'text-text-secondary';
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return 'TrendingUp';
      case 'down': return 'TrendingDown';
      default: return 'Minus';
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'up': return 'text-success';
      case 'down': return 'text-error';
      default: return 'text-text-secondary';
    }
  };

  const getChangeColor = (changeType) => {
    switch (changeType) {
      case 'positive': return 'text-success';
      case 'negative': return 'text-error';
      default: return 'text-text-secondary';
    }
  };

  const getUtilizationColor = (utilization) => {
    if (utilization >= 85) return 'text-success';
    if (utilization >= 70) return 'text-warning-600';
    return 'text-error';
  };

  return (
    <div className="space-y-6">
      {/* Key Analytics */}
      <div className="bg-surface rounded-lg shadow-card border border-border">
        <div className="p-6 border-b border-border">
          <h2 className="text-lg font-heading font-semibold text-text-primary">Practice Analytics</h2>
          <p className="text-sm text-text-secondary mt-1">Performance insights for {selectedPeriod} period</p>
        </div>

        <div className="p-6">
          <div className="space-y-6">
            {analyticsData?.map((metric) => (
              <div key={metric?.id} className="flex items-center justify-between p-4 bg-secondary-50 rounded-lg hover:bg-secondary-100 transition-smooth">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center">
                    <Icon name={metric?.icon} size={20} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-text-primary">{metric?.title}</h3>
                    <p className="text-xs text-text-secondary mt-1">{metric?.description}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-text-primary">{metric?.value}</p>
                  <p className={`text-xs font-medium ${getChangeColor(metric?.changeType)}`}>
                    {metric?.change}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Matter Profitability */}
      <div className="bg-surface rounded-lg shadow-card border border-border">
        <div className="p-6 border-b border-border">
          <h3 className="text-lg font-heading font-semibold text-text-primary">Matter Type Profitability</h3>
          <p className="text-sm text-text-secondary mt-1">Revenue analysis by practice area</p>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            {matterProfitability?.map((matter, index) => (
              <div key={index} className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-secondary-50 transition-smooth">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-text-primary">{matter?.type}</h4>
                    <div className="flex items-center space-x-2">
                      <span className={`text-xs font-medium ${getProfitColor(matter?.profit)}`}>
                        {matter?.profit} Profit
                      </span>
                      <Icon 
                        name={getTrendIcon(matter?.trend)} 
                        size={14} 
                        className={getTrendColor(matter?.trend)}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-text-secondary">
                    <span>{matter?.volume} cases</span>
                    <span>Avg: KES {(matter?.avgValue / 1000).toFixed(0)}K</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Resource Utilization */}
      <div className="bg-surface rounded-lg shadow-card border border-border">
        <div className="p-6 border-b border-border">
          <h3 className="text-lg font-heading font-semibold text-text-primary">Resource Utilization</h3>
          <p className="text-sm text-text-secondary mt-1">Staff efficiency and billing metrics</p>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            {resourceMetrics?.map((resource, index) => (
              <div key={index} className="p-4 border border-border rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-text-primary">{resource?.resource}</h4>
                  <span className="text-xs text-text-secondary">{resource?.count} staff</span>
                </div>
                
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-text-secondary">Utilization</span>
                  <span className={`text-sm font-medium ${getUtilizationColor(resource?.utilization)}`}>
                    {resource?.utilization}%
                  </span>
                </div>
                
                <div className="w-full bg-secondary-200 rounded-full h-2 mb-3">
                  <div
                    className={`h-2 rounded-full ${
                      resource?.utilization >= 85 ? 'bg-success' :
                      resource?.utilization >= 70 ? 'bg-warning-500' : 'bg-error'
                    }`}
                    style={{ width: `${resource?.utilization}%` }}
                  ></div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-text-secondary">Hourly Rate</span>
                  <span className="text-sm font-medium text-text-primary">
                    KES {resource?.billableRate?.toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PracticeAnalytics;